// src/pages/healthcare/results/ResultsPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import { can, canUseOperatorTools } from "../../../utils/userPrefs/validatePermissions";
import { Space, Button, Dropdown, Typography } from "antd";
import { PlusOutlined, EyeOutlined, PrinterOutlined, UserOutlined, SyncOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import type { ColumnsType } from "antd/es/table";

import { fetchTestsCompany, TableFilterSort } from "../../../api/healthcare/Testing/TestApi";
import { reAnalyseSample } from "../../../api/labiq/SampleApi";

import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import PageToolbar from "../../../components/shared/PageToolbar";
import DataTable from "../../../components/shared/DataTable";
import StatusTag from "../../../components/shared/StatusTag";
import CreateTestModal from "./CreateTestOrderModal";
import PrintOrderModal from "./PrintOrderModal";
import EditPatientModal from "../patient/PatientModal";

import { Test, STATUS_OPTIONS, PRIORITY_OPTIONS } from "../../../types/healthcare/TestTypes";
import { useNotifications } from "../../../context/NotificationContext";
import { useXoeTestOrderFocus } from "../../../hooks/xoe/useXoeTestOrderFocus";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import { Patient } from "../../../types/healthcare/PatientTypes";

const { Text } = Typography;

const ResultsPage: React.FC = () => {
	const [tests, setTests] = useState<Test[]>([]);
	const [selectedTests, setSelectedTests] = useState<Test[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [currentTest, setCurrentTest] = useState<Partial<Test | null>>(null);

	const [isPatientModalOpen, setIsPatientModalOpen] = useState<boolean>(false);
	const [currentPatient, setCurrentPatient] = useState<Patient | null>(null);

	const [printModalOpen, setPrintModalOpen] = useState<boolean>(false);

	const { showError, showSuccess } = useNotifications();

	const [pageNumber, setPageNumber] = useState<number>(1);
	const [totalResults, setTotalResults] = useState<number>(0);
	const [sortField, setSortField] = useState<string | undefined>(undefined);
	const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | undefined>(undefined);

	// Toolbar filter state
	const [patientSearch, setPatientSearch] = useState<string>("");
	const [sampleSearch, setSampleSearch] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [priorityFilter, setPriorityFilter] = useState<string>("");

	const RESULTS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

	const loadTests = useCallback(async () => {
		setIsLoading(true);
		try {
			const tableParams: TableFilterSort = {
				filters: {
					...(patientSearch ? { patient_name: patientSearch } : {}),
					...(sampleSearch ? { sample_id: [sampleSearch] } : {}),
					...(statusFilter ? { status: [statusFilter] } : {}),
					...(priorityFilter ? { priority: [priorityFilter] } : {}),
				},
				...(sortField && sortOrder ? { sorter: { field: sortField, order: sortOrder } } : {}),
			};
			const { data, total } = await fetchTestsCompany(pageNumber, RESULTS_PER_PAGE, undefined, tableParams);
			setTests(data);
			setTotalResults(total);
		} catch {
			showError("Failed to load test results. Please try again later.");
		} finally {
			setIsLoading(false);
		}
	}, [pageNumber, patientSearch, sampleSearch, statusFilter, priorityFilter, sortField, sortOrder]);

	useEffect(() => {
		loadTests();
	}, [loadTests]);

	// Opening a test order binds Xoe to the patient it is for, the same way opening a patient
	// record does (86d3z0ty3 / X1). Published from the page rather than from the modal — see
	// the hook's docblock for why the two in-PatientModal render sites must not.
	useXoeTestOrderFocus(isModalOpen, currentTest);

	const handleEditClick = useCallback((test: Test) => {
		setCurrentTest(test);
		setIsModalOpen(true);
	}, []);

	const handleNewTestOrder = useCallback(() => {
		setCurrentTest({
			patient_id: "",
			test_type: [],
			priority: "routine",
			status: "ordered",
		});
		setIsModalOpen(true);
	}, []);

	const handleModalClose = useCallback(() => {
		setIsModalOpen(false);
		setCurrentTest(null);
		setSelectedTests([]);
	}, []);

	const handleSaveTest = useCallback(
		async (_savedTest: Test) => {
			await loadTests();
			handleModalClose();
		},
		[loadTests, handleModalClose],
	);

	const handleOpenPatient = (patient: Patient) => {
		setCurrentPatient(patient);
		setIsPatientModalOpen(true);
	};

	const handleReset = () => {
		setPatientSearch("");
		setSampleSearch("");
		setStatusFilter("");
		setPriorityFilter("");
		setPageNumber(1);
	};

	const reAnalyseData = async (sampleId: string) => {
		if (!sampleId) return;
		setIsLoading(true);
		try {
			await reAnalyseSample(sampleId);
			showSuccess("Re-analysis request run successfully");
		} catch {
			showError("Error analysing the sample, please retry or contact support");
		} finally {
			setIsLoading(false);
		}
	};

	const printMenuItems = [
		{
			key: "print-selected",
			label: "Print Selected",
			icon: <PrinterOutlined />,
			onClick: () => setPrintModalOpen(true),
		},
	];

	const isAdminUser = canUseOperatorTools() && can("labiq.samples.write");

	const columns: ColumnsType<Test> = [
		{
			title: "Patient",
			key: "patient_name",
			fixed: "left",
			width: 170,
			render: (_, record) =>
				record.patient ? (
					<Text style={{ fontWeight: 500, fontSize: 12 }}>
						{record.patient.first_name} {record.patient.last_name}
					</Text>
				) : (
					<Text type="secondary" style={{ fontSize: 12 }}>Unknown</Text>
				),
		},
		{
			title: "Sample ID",
			dataIndex: "sample_id",
			key: "sample_id",
			width: 130,
			render: (text: string) => (
				<Text className="mg-mono-num" style={{ fontSize: 12 }}>
					{text || "—"}
				</Text>
			),
		},
		{
			title: "Test Package",
			key: "test_package",
			width: 200,
			render: (_, record) =>
				record.custom_test_packages?.length ? (
					<Text style={{ fontSize: 12 }}>
						{record.custom_test_packages.map((p) => p.name).join(", ")}
					</Text>
				) : (
					<Text type="secondary" style={{ fontSize: 12 }}>—</Text>
				),
		},
		{
			title: "Priority",
			dataIndex: "priority",
			key: "priority",
			width: 100,
			render: (priority: string) => <StatusTag status={priority} />,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 160,
			sorter: true,
			render: (status: string) => <StatusTag status={status} />,
		},
		{
			title: "Abnormal",
			key: "is_abnormal",
			width: 100,
			align: "center",
			render: (_, record) => {
				const anyAbnormal = record.test_results?.some((r) => r.is_abnormal);
				return anyAbnormal ? <StatusTag status="failed" label="Abnormal" size="small" /> : null;
			},
		},
		{
			title: "Follow-up",
			key: "requires_followup",
			width: 100,
			align: "center",
			render: (_, record) => {
				const anyFollowup = record.test_results?.some((r) => r.requires_followup);
				return anyFollowup ? <StatusTag status="waiting" label="Required" size="small" /> : null;
			},
		},
		{
			title: "Results Date",
			key: "results_available_date",
			width: 120,
			sorter: true,
			render: (_, record) => {
				const earliest = record.test_results?.reduce(
					(min, r) =>
						r.results_available_date
							? !min || new Date(r.results_available_date) < min
								? new Date(r.results_available_date)
								: min
							: min,
					null as Date | null,
				);
				return earliest ? (
					<Text style={{ fontSize: 12 }}>{format(earliest, "dd/MM/yyyy")}</Text>
				) : (
					<Text type="secondary" style={{ fontSize: 12 }}>—</Text>
				);
			},
		},
		{
			title: "Order Date",
			dataIndex: "created_at",
			key: "created_at",
			width: 110,
			sorter: true,
			render: (date: string) =>
				date ? (
					<Text style={{ fontSize: 12 }}>{format(new Date(date), "dd/MM/yyyy")}</Text>
				) : (
					<Text type="secondary" style={{ fontSize: 12 }}>—</Text>
				),
		},
		{
			title: "",
			key: "actions",
			width: 80,
			align: "center",
			render: (_, record) => (
				<Space size={2}>
					{record.patient && (
						<Button
							type="text"
							size="small"
							icon={<UserOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleOpenPatient(record.patient!);
							}}
							title="Open patient profile"
						/>
					)}
					{isAdminUser && (
						<Button
							type="text"
							size="small"
							icon={isLoading ? <SyncOutlined spin /> : <PlusCircleOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								reAnalyseData(record.sample_id || "");
							}}
							title="Re-analyse data"
						/>
					)}
				</Space>
			),
		},
	];

	return (
		<Layout>
			<PageContainer data-testid="results-root">
				<PageToolbar
					primaryAction={{ label: "New Test Order", icon: <PlusOutlined />, onClick: handleNewTestOrder, testId: "results-create-btn" }}
					extraActions={[
						selectedTests.length === 1 && {
							label: "View Details",
							icon: <EyeOutlined />,
							onClick: () => handleEditClick(selectedTests[0]),
						},
						selectedTests.length > 0 && {
							label: (
								<Dropdown menu={{ items: printMenuItems }} trigger={["click"]}>
									<Space>
										<PrinterOutlined />
										Print
									</Space>
								</Dropdown>
							),
							onClick: () => {},
						},
					].filter(Boolean) as any[]}
					filters={[
						{
							type: "search",
							placeholder: "Search patient…",
							value: patientSearch,
							onChange: (v: string) => {
								setPatientSearch(v);
								setPageNumber(1);
							},
							width: 220,
						},
						{
							type: "search",
							placeholder: "Search sample ID…",
							value: sampleSearch,
							onChange: (v: string) => {
								setSampleSearch(v);
								setPageNumber(1);
							},
							width: 200,
						},
						{
							type: "select",
							placeholder: "Filter by status",
							options: STATUS_OPTIONS,
							value: statusFilter || undefined,
							onChange: (v: string) => {
								setStatusFilter(v);
								setPageNumber(1);
							},
							width: 180,
							allowClear: true,
						},
						{
							type: "select",
							placeholder: "Filter by priority",
							options: PRIORITY_OPTIONS,
							value: priorityFilter || undefined,
							onChange: (v: string) => {
								setPriorityFilter(v);
								setPageNumber(1);
							},
							width: 160,
							allowClear: true,
						},
					]}
					onReset={handleReset}
				/>

				<DataTable
					columns={columns}
					dataSource={tests}
					rowKey="id"
					loading={isLoading}
					onRowClick={handleEditClick}
					selectedRows={selectedTests}
					setSelectedRows={setSelectedTests}
					total={totalResults}
					currentPage={pageNumber}
					pageSize={RESULTS_PER_PAGE}
					onPageChange={(page) => setPageNumber(page)}
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={(field, order) => {
						setSortField(field);
						setSortOrder(order);
					}}
					emptyTitle="No test orders found"
					emptyDescription="Create a new test order or adjust your filters."
					scrollX={1300}
					fillHeight
				/>

				{isModalOpen && currentTest && (
					<CreateTestModal
						isOpen={isModalOpen}
						onClose={handleModalClose}
						onSave={handleSaveTest}
						currentOrder={currentTest}
						setCurrentOrder={setCurrentTest}
						patient={currentTest.patient}
					/>
				)}

				{isPatientModalOpen && currentPatient && (
					<EditPatientModal
						isOpen={isPatientModalOpen}
						onClose={() => {
							setIsPatientModalOpen(false);
							setCurrentPatient(null);
						}}
						patient={currentPatient}
					/>
				)}

				{printModalOpen && selectedTests.length > 0 && (
					<PrintOrderModal
						isOpen={printModalOpen}
						onClose={() => setPrintModalOpen(false)}
						orderIds={selectedTests.map((t) => t.id)}
						title="Print Test Orders"
					/>
				)}
			</PageContainer>
		</Layout>
	);
};

export default ResultsPage;
